/**
 * Crypto utilities for SharePass Reveal
 * AES-256-GCM decryption with format: [IV(12 bytes) || ciphertext]
 */

const IV_LENGTH = 12;

/**
 * Convert base64/base64url string to Uint8Array
 * Handles both standard base64 and base64url encoding
 */
export const base64ToBytes = (input: string): Uint8Array => {
    if (!input) {
        throw new Error('base64ToBytes: empty input');
    }

    // Normalize base64url to base64
    let normalized = input.replace(/-/g, '+').replace(/_/g, '/');

    // Add padding if needed
    const pad = normalized.length % 4;
    if (pad === 2) normalized += '==';
    else if (pad === 3) normalized += '=';

    try {
        const binaryString = atob(normalized);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
    } catch {
        throw new Error('base64ToBytes: invalid base64 string');
    }
};

/**
 * Convert Uint8Array to base64 string
 */
export const bytesToBase64 = (bytes: Uint8Array): string => {
    return btoa(String.fromCharCode(...bytes));
};

/**
 * Convert UTF-8 string to Uint8Array
 */
export const utf8ToBytes = (str: string): Uint8Array => {
    return new TextEncoder().encode(str);
};

/**
 * Convert Uint8Array to UTF-8 string
 */
export const bytesToUtf8 = (bytes: Uint8Array): string => {
    return new TextDecoder().decode(bytes);
};

/**
 * Derive AES key from key input (base64 string or passphrase)
 * - If input decodes to 16 or 32 bytes, use directly
 * - Otherwise, derive 32 bytes via SHA-256
 */
export const deriveKeyBytes = async (keyInput: string): Promise<Uint8Array> => {
    try {
        const decoded = base64ToBytes(keyInput);
        if (decoded.length === 16 || decoded.length === 32) {
            return decoded;
        }
    } catch {
        // Not valid base64, treat as passphrase
    }

    // Derive 32 bytes from passphrase using SHA-256
    const encoded = utf8ToBytes(keyInput);
    const hash = await crypto.subtle.digest('SHA-256', encoded as unknown as ArrayBuffer);
    return new Uint8Array(hash);
};

/**
 * Import key bytes as CryptoKey for AES-GCM
 */
export const importAesKey = async (keyBytes: Uint8Array): Promise<CryptoKey> => {
    return crypto.subtle.importKey('raw', keyBytes as unknown as ArrayBuffer, 'AES-GCM', false, ['decrypt']);
};

/**
 * Decrypt AES-GCM encrypted data
 * Input format: [IV(12 bytes) || ciphertext]
 */
export const decryptAesGcm = async (encryptedData: Uint8Array, aesKey: CryptoKey): Promise<Uint8Array> => {
    if (encryptedData.length <= IV_LENGTH) {
        throw new Error('Invalid encrypted data: too short');
    }

    const iv = encryptedData.slice(0, IV_LENGTH);
    const ciphertext = encryptedData.slice(IV_LENGTH);

    const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: iv as unknown as ArrayBuffer },
        aesKey,
        ciphertext as unknown as ArrayBuffer
    );

    return new Uint8Array(decrypted);
};

/**
 * Main decryption function for secrets
 * @param encryptedBase64 - Base64 encoded encrypted data [IV || ciphertext]
 * @param decryptionKey - Base64 key or passphrase (randomWord from URL)
 * @returns Decrypted string content
 */
export const decryptSecret = async (encryptedBase64: string, decryptionKey: string): Promise<string> => {
    // Derive key bytes from input
    const keyBytes = await deriveKeyBytes(decryptionKey);

    // Import as CryptoKey
    const aesKey = await importAesKey(keyBytes);

    // Decode encrypted data from base64
    const encryptedData = base64ToBytes(encryptedBase64);

    // Decrypt
    const decrypted = await decryptAesGcm(encryptedData, aesKey);

    // Convert to string
    return bytesToUtf8(decrypted);
};

/**
 * Decrypt file from ArrayBuffer
 * @param encryptedData - Raw encrypted bytes
 * @param decryptionKey - Base64 key or passphrase
 * @returns Decrypted ArrayBuffer
 */
export const decryptFile = async (encryptedData: ArrayBuffer, decryptionKey: string): Promise<ArrayBuffer> => {
    const keyBytes = await deriveKeyBytes(decryptionKey);
    const aesKey = await importAesKey(keyBytes);
    const decrypted = await decryptAesGcm(new Uint8Array(encryptedData), aesKey);
    // Create a new ArrayBuffer from the decrypted data
    const result = new ArrayBuffer(decrypted.length);
    new Uint8Array(result).set(decrypted);
    return result;
};
